import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { CloseIcon } from "components";
import { memo } from "react";
import { stripHtml } from "./utils/stripHtml";

interface ReplyPreviewProps {
  senderDisplayName: string;
  messageHtml: string;
  onClose: () => void;
}

export const ReplyPreview = memo(function ReplyPreview({
  senderDisplayName,
  messageHtml,
  onClose,
}: ReplyPreviewProps) {
  const theme = useTheme();
  const plainText = stripHtml(messageHtml);
  // Fallback for attachment-only messages
  const displayText = plainText.trim() || "Attachment";

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: "flex-start",
        gap: "8px",
        p: "8px",
        mx: "12px",
        mt: "8px",
        borderRadius: "8px",
        border: `1px solid ${theme.palette.neutral["010"]}`,
        boxShadow: "0px 0px 4px 0px #0000000F, 0px 0px 2px 0px #0000000A",
        position: "relative",
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          gap: "4px",
          borderLeft: `4px solid ${theme.palette.neutral["010"]}`,
          pl: "8px",
          py: "2px",
          position: "relative",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="caption-2">{senderDisplayName}</Typography>
          <IconButton
            aria-label="Close reply preview"
            onClick={onClose}
            sx={{
              flexShrink: 0,
              p: 0,
            }}
          >
            <CloseIcon
              sx={{ width: "16px", height: "16px" }}
              htmlColor={theme.palette.lightBg.low}
            />
          </IconButton>
        </Stack>
        <Typography
          variant="body-small"
          sx={{
            color: theme.palette.lightBg.medium,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            wordBreak: "break-word",
          }}
        >
          {displayText}
        </Typography>
      </Box>
    </Stack>
  );
});
